"use client"

import type React from "react"

import { useState, useEffect, useRef, useCallback } from "react"
import { useRouter } from "next/navigation"
import { Search, X, MapPin, Building2, Home } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { LocationDropdownInline } from "@/components/search/location-dropdown"

interface QuickResult {
  type: 'property' | 'project' | 'location' | 'suggestion'
  id: string
  title: string
  subtitle?: string
  url: string
  image?: string
}

export function HeroSearchBar() {
  const router = useRouter()
  const [searchQuery, setSearchQuery] = useState("")
  const [suggestions, setSuggestions] = useState<QuickResult[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [listingType, setListingType] = useState("sale")
  const [propertyType, setPropertyType] = useState("")
  const [priceRange, setPriceRange] = useState("")
  const [selectedLocation, setSelectedLocation] = useState<{ city?: string }>({})
  const [isLoading, setIsLoading] = useState(false)

  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch suggestions from API with debounce
  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const response = await fetch(`/api/search/quick?q=${encodeURIComponent(query)}`)
      const data = await response.json()

      if (data.success && data.results) {
        setSuggestions(data.results)
        setShowSuggestions(data.results.length > 0)
        setSelectedIndex(-1)
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error)
      setSuggestions([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Handle input change with 300ms debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchQuery(query)

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    if (query.length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      setIsLoading(false)
      return
    }

    // Set loading state immediately
    setIsLoading(true)

    // Debounce API call by 300ms
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(query)
    }, 300)
  }

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex])
        } else {
          handleSearch(e as any)
        }
        break
      case 'Escape':
        setShowSuggestions(false)
        setSelectedIndex(-1)
        inputRef.current?.blur()
        break
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const params = new URLSearchParams()

    params.append("listing_type", listingType === "sale" ? "For Sale" : "For Rent")

    if (searchQuery) {
      params.append("search", searchQuery)
    }

    // Add property type filter
    if (propertyType) {
      params.append("property_type", propertyType)
    }

    // Add location filter if selected
    if (selectedLocation.city) {
      params.append("city", selectedLocation.city)
    }

    // Add price range filter
    if (priceRange) {
      const [min, max] = priceRange.split('-')
      if (min && min !== '0') {
        params.append("min_price", (parseFloat(min) * 1000000).toString())
      }
      if (max && max !== '+' && max.toLowerCase() !== 'plus') {
        params.append("max_price", (parseFloat(max) * 1000000).toString())
      }
      // Handle "500+" format
      if (max === '+' || max?.toLowerCase() === 'plus') {
        params.append("min_price", (parseFloat(min) * 1000000).toString())
      }
    }

    router.push(`/properties?${params.toString()}`)
    setShowSuggestions(false)
    setSelectedIndex(-1)
  }

  const handleSuggestionClick = (suggestion: QuickResult) => {
    router.push(suggestion.url)
    setShowSuggestions(false)
    setSelectedIndex(-1)
  }

  const getListingTypeText = () => {
    return listingType === "sale" ? "For Sale" : "For Rent"
  }

  // Get icon based on suggestion type
  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case 'property':
        return <Home className="h-4 w-4 text-gray-400" />
      case 'project':
        return <Building2 className="h-4 w-4 text-gray-400" />
      case 'location':
        return <MapPin className="h-4 w-4 text-gray-400" />
      default:
        return <Search className="h-4 w-4 text-gray-400" />
    }
  }

  // Get category label
  const getCategoryLabel = (type: string) => {
    switch (type) {
      case 'property':
        return 'Property'
      case 'project':
        return 'Project'
      case 'location':
        return 'Location'
      default:
        return ''
    }
  }

  return (
    <div className="relative w-full max-w-4xl mx-auto" ref={searchRef}>
      {/* Listing type toggle - Buy/Rent only */}
      <div className="bg-white/20 backdrop-blur-sm rounded-full p-1 mb-4 flex w-full max-w-xs mx-auto">
        <button
          type="button"
          onClick={() => setListingType("sale")}
          className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-colors ${
            listingType === "sale" ? "bg-white text-primary shadow-sm" : "text-white hover:bg-white/10"
          }`}
        >
          For Sale
        </button>
        <button
          type="button"
          onClick={() => setListingType("rent")}
          className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-colors ${
            listingType === "rent" ? "bg-white text-primary shadow-sm" : "text-white hover:bg-white/10"
          }`}
        >
          For Rent
        </button>
      </div>

      {/* Search form */}
      <div className="w-full">
        <div className="relative">
          <form onSubmit={handleSearch} className="relative">
            <div className="flex flex-col md:flex-row gap-3 md:gap-0 bg-white rounded-lg shadow-lg transition-all p-3 md:p-0">
              {/* Location Dropdown */}
              <div className="hidden md:block md:w-48">
                <LocationDropdownInline
                  onLocationChange={setSelectedLocation}
                  className="h-full border-0 focus:ring-0"
                />
              </div>

              {/* Search Input */}
              <div className="flex items-center flex-1">
                <div className="flex-shrink-0 pl-2 md:pl-4">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>

                <Input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Search by property code, project, location..."
                  className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 py-3 md:py-6 px-3 md:px-4 text-base md:text-lg"
                  autoComplete="off"
                />

                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("")
                      setSuggestions([])
                      setShowSuggestions(false)
                    }}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>

              {/* Search Button */}
              <Button type="submit" className="w-full md:w-auto md:m-1 rounded-md text-sm py-3 md:py-2">
                Search {getListingTypeText()}
              </Button>
            </div>
          </form>

          {/* Auto-suggestions Dropdown */}
          {showSuggestions && (
            <Card className="absolute top-full left-0 right-0 mt-1 z-50 border-0 shadow-xl">
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="py-4 px-4 text-center text-gray-500 text-sm">
                    Searching...
                  </div>
                ) : (
                  <ul className="py-2 max-h-[400px] overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                      <li key={suggestion.id}>
                        <button
                          type="button"
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3 transition-colors ${
                            index === selectedIndex ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => handleSuggestionClick(suggestion)}
                          onMouseEnter={() => setSelectedIndex(index)}
                        >
                          {/* Image OR Icon on Left */}
                          <div className="flex-shrink-0">
                            {suggestion.image ? (
                              <img
                                src={suggestion.image}
                                alt=""
                                className="w-12 h-12 rounded object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded">
                                {getSuggestionIcon(suggestion.type)}
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 truncate">
                                {suggestion.title}
                              </span>
                              {suggestion.type !== 'suggestion' && (
                                <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                  {getCategoryLabel(suggestion.type)}
                                </span>
                              )}
                            </div>
                            {suggestion.subtitle && (
                              <div className="text-sm text-gray-500 truncate mt-0.5">
                                {suggestion.subtitle}
                              </div>
                            )}
                          </div>
                        </button>
                      </li>
                    ))}

                    {/* Keyboard navigation hint */}
                    <li className="px-4 py-2 text-xs text-gray-400 border-t mt-2">
                      <div className="flex items-center justify-between">
                        <span>Use ↑↓ to navigate, Enter to select, Esc to close</span>
                      </div>
                    </li>
                  </ul>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
